import { i18n } from '@/locales';
import dateFormatType from '@/constants/dateFormatType';
import { addDays, addHours, addMinutes, addMonths, differenceInMilliseconds, format, isAfter, parse, subDays, subHours, subMonths, } from 'date-fns';
/**
 * 10보다 작으면 문자열 0을 추가한다.
 */
const addZero = (v) => (v < 10 ? `0${v}` : v);
/**
 * 시간대 리스트
 * ['09~10', '10~11' ... '07~08']
 * @param isExcludeTotal 합계 제외 여부 true => 제외, false (기본값) => 포함
 * @returns
 */
export const getHourList = (isExcludeTotal = false) => {
    const arrayHead = isExcludeTotal
        ? []
        : [
            '합계',
        ];
    // 9부터 23까지의 배열 생성
    const array1 = Array.from({
        length: 23 - 9 + 1,
    }, (_, i) => 9 + i);
    // 0부터 8까지의 배열 생성
    const array2 = Array.from({
        length: 9,
    }, (_, i) => i);
    // 두 배열을 붙이기
    return arrayHead
        .concat(array1, array2)
        .reduce((acc, hour) => {
        if (hour === '합계') {
            acc.push(hour);
        }
        else {
            acc.push(`${addZero(hour)}~${addZero(hour + 1)}`);
        }
        return acc;
    }, []);
};
/**
 * 월부터 일요일까지 배열 생성
 * @param isExcludeTotal 합계 제외 여부 true => 제외, false (기본값) => 포함
 * @returns
 */
export const getWeekList = (isExcludeTotal = false) => {
    const list = isExcludeTotal
        ? []
        : [
            {
                title: '합계',
                value: 'total',
            },
        ];
    list.push({
        title: '월',
        value: 'mon',
    }, {
        title: '화',
        value: 'tue',
    }, {
        title: '수',
        value: 'wed',
    }, {
        title: '목',
        value: 'thu',
    }, {
        title: '금',
        value: 'fri',
    }, {
        title: '토',
        value: 'sat',
    }, {
        title: '일',
        value: 'sun',
    });
    return list;
};
export const validateDate = (f, nargs) => (...args) => {
    // Date 객체 대응
    if (args[0] instanceof Date) {
        return f.call(null, ...args);
    }
    // timestamp 전용
    if (args.length === nargs && args.every((it) => Number.isInteger(it))) {
        return f.call(null, ...args);
    }
    return 0;
};
export const beforeDays = validateDate((time, days) => subDays(time, days).setHours(0, 0, 0, 0), 2);
export const afterDays = validateDate((time, days) => addDays(time, days).setHours(23, 59, 59, 999), 2);
export const beforeMonths = validateDate((time, days) => subMonths(time, days));
export const afterMonths = validateDate((time, days) => addMonths(time, days));
export const beforeHours = validateDate((time, hours) => subHours(time, hours).setMilliseconds(0), 2);
// 유효하지 않는 시간 인자가 들어왔을 경우 지나지 않은 것으로 판단
export const isAfterHours = (time, hours) => !!validateDate(() => isAfter(Date.now(), addHours(time, hours)), 2)(time, hours);
export const addMinutesToDate = validateDate((time, minutes) => addMinutes(time, minutes).getTime(), 2);
/**
 * 조회기간 date-picker에서 최대 선택 가능 월 초기 설정하기
 * 한국시간 매월 1일 3시 이후부터 전월(1일~말일)의 정산 데이터를 확인할 수 있음
 * ex) 한국시간 기준 11월 1일 3시 이후부터 한국시간 기준 10월 1일 ~ 10월 31일 조회 가능
 * v-date-picker value에 맞게 포멧팅된 최대 선택 가능 월 ex) '2023-01'
 */
export const setDefaultSelectedMonthMaxDate = (limitTime) => {
    const today = new Date();
    const todayDate = today.getDate();
    const todayHour = today.getHours();
    let subDay = 1;
    if (todayDate === 1 && todayHour < limitTime) {
        subDay = 2;
    }
    return format(beforeMonths(today, subDay), dateFormatType.MONTH);
};
/**
 * 경과 시간 ms 계산
 */
export const elapsedTimeInMilliseconds = validateDate((time) => {
    const now = Date.now();
    return differenceInMilliseconds(now, time);
}, 1);
/**
 * 경과 시간을 양수, 음수 시간, 분으로 출력한다
 */
export const makeTimeFactor = (time) => {
    let remainder = Math.abs(time) / 1_000;
    const hours = Math.floor(remainder / 3_600);
    remainder -= hours * 3_600;
    const minutes = Math.round(remainder / 60);
    return {
        positive: time > 0,
        hours,
        minutes,
    };
};
export const convertTimeFactorToText = ({ positive = true, hours = 0, minutes = 0 }) => ({
    positive,
    hours,
    hoursText: i18n.t('views.order_detail.order_detail_delivery_info.suffix_hours', {
        hours,
    }),
    minutes,
    minutesText: i18n.t('views.order_detail.order_detail_delivery_info.suffix_minutes', {
        minutes,
    }).toString(),
});
/**
 * Description
 * @param {Object} timeFactor
 * @param {boolean} timeFactor.positive - 남은 시간과 지난 시간 부호
 * @param {number} timeFactor.hours - 시간 수치
 * @param {string} timeFactor.hoursText - 시간 문자
 * @param {number} timeFactor.minutes - 분 수치
 * @param {string} timeFactor.minutesText - 분 문자
 * @returns {string} 시간 분 문자 펴현
 */
export const getTimeText = (timeFactor) => {
    const { positive, hours, hoursText, minutes, minutesText, } = convertTimeFactorToText(timeFactor);
    let result = minutesText;
    if (hours === 0 && minutes === 0) {
        return result;
    }
    if (hours > 0) {
        result = `${hoursText} ${result}`;
    }
    return positive ? result : `-${result}`;
};
export const remainingTimeFactor = (time) => makeTimeFactor(elapsedTimeInMilliseconds(time) * -1);
export const elapsedTimeFactor = (time) => makeTimeFactor(elapsedTimeInMilliseconds(time));
export const getRemainingTime = (time) => {
    const factor = remainingTimeFactor(time);
    const text = getTimeText(factor);
    return {
        text,
        factor,
    };
};
export const getRemainingTimeText = (time) => {
    const { text } = getRemainingTime(time);
    return text;
};
/**
 * TODO: 추후 utils 아래 fromDateString, fromDateNumber를 이것으로 대체
 * @param dateString 입력값
 * @param inputForm 입력값 format 예) yyyymmdd src/constants/dateFormatType.ts
 * @param outputForm 출력값 format 예) yyyymmdd
 * @returns
 */
export const getFormattedDate = (dateString, inputForm, outputForm) => {
    const parsedDate = parse(dateString, inputForm, new Date());
    const formattedDate = format(parsedDate, outputForm);
    return formattedDate;
};
/**
 * api response 중 2023-05-27 같은 문자형태로 넘어오는 일자를 Date로 수정
 */
export const fromDateString = (dateString) => {
    const arr = dateString.split('-');
    const year = Number.parseInt(arr[0], 10);
    const month = Number.parseInt(arr[1], 10) - 1;
    const date = Number.parseInt(arr[2], 10);
    return new Date(year, month, date).getTime();
};
/**
 *  * @param {number} dateNumber 숫자형태의 일자 (ex 20230527)
 *  * @param {string} dateFormat 출력하고자 하는 dateFormat형태 (ex yyyy-MM-dd)
 *  @returns dateFormat 유무에 따라 Date 또는 string으로 일자반환
 */
export const fromDateNumber = (dateNumber, _dateFormat) => {
    const str = dateNumber.toString();
    const year = Number(str.substring(0, 4));
    const month = Number(str.substring(4, 6)) - 1;
    const date = Number(str.substring(6, 8));
    const newDateForm = new Date(year, month, date).getTime();
    return _dateFormat ? format(newDateForm, _dateFormat) : newDateForm;
};
